import { observer } from 'mobx-react-lite';
import React from 'react';

import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { IconStars } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import logoImage from 'base/ui/logo.file.svg';
import { DividerLine } from 'base/ui/panel';
import { Textarea } from 'base/ui/textarea';
import {
  TextMedium,
  TextRegularLight,
  TextSmallLight,
  TextTinyLight,
} from 'base/ui/typography';

import { Assistant } from './ai/assistant';
import { spaceIsEmpty } from './ai/page_sections';
import {
  getCompanyDescriptionError,
  LimitCounter,
  PROJECT_BRIEF_LIMIT,
} from './ai/validation';
import { ProjectEditingActions } from './editing_actions/project';
import { editorModeStore, isWireframeOrDesign } from './editor_mode';
import { DEFAULT_GENERATE_PAGE_COUNT } from './edit_panel/space';
import { Space } from './model';
import { selectionLocale, SelectionStore } from './selection/store';
import { ProjectSession } from './session/session';
import { SidebarNavigation } from './sidebar/routes';

import { currentUrl } from 'blocks/current_url';
import styles from './experiment_prompt.css';

const BRIEF_EXAMPLES = [
  'Cloudify is a cloud-based accounting software product that lets small-business owners manage their finances from anywhere and integrate with more than 1,000 apps.',
  'Rebank is a digital bank that offers a range of financial services primarily through a mobile app, allowing you to manage your money on-the-go.',
  'Gretta is a boutique Architectural firm based in Los Angeles that focuses on homes as well as smaller commercial and community projects.',
];

export const EXPRIMENT_QUERY_PARAM = 'exp';

interface ExperimentPromptProps {
  isExperiment: boolean;
  assistant: Assistant;
  navigation: SidebarNavigation;
  selection: SelectionStore;
  session: ProjectSession;
  editing?: ProjectEditingActions;
}

// As part of the experiment 6 EXP6 by the growth team
export function ExperimentPrompt({
  navigation,
  selection,
  isExperiment,
  ...props
}: ExperimentPromptProps) {
  const { project } = selection;
  const spaceEmpty = spaceIsEmpty(project, selection.get().space || 'home');

  // only show the dialog when the user opens the editor with an empty project
  //   and the experiment query param is present
  const [open, setOpen] = React.useState(spaceEmpty && isExperiment);

  return (
    <>
      {Boolean(open) && (
        <ExperimentPromptDialog
          onClose={() => {
            setOpen(false);
            navigation.goto({ path: 'edit' });

            // Remove the exp query param from the URL
            const params = currentUrl.searchParams;
            params.delete(EXPRIMENT_QUERY_PARAM);
            window.history.replaceState(
              null,
              '',
              decodeURIComponent(`${currentUrl.pathname}?${params}`),
            );
          }}
          selection={selection}
          {...props}
        />
      )}
    </>
  );
}

interface ExperimentPromptDialogProps {
  assistant: Assistant;
  selection: SelectionStore;
  session: ProjectSession;
  editing?: ProjectEditingActions;
  onClose?: () => void;
}

export const ExperimentPromptDialog = observer(
  function _ExperimentPromptDialog({
    assistant,
    selection,
    session,
    editing,
    onClose,
  }: ExperimentPromptDialogProps) {
    const { project } = selection;
    const sel = selection.get();
    const space = sel.space || 'home';
    const spaceEditing = editing?.forSpace(space);
    const locale = selectionLocale({ space }, selection.project);

    const companyDescription =
      (space === 'home' ? project.brief : space.description) || '';
    // don't show the error when the user opens this panel on an empty project
    // - that's a bad UX
    const hideError = companyDescription.length < 3;
    const error = getCompanyDescriptionError(companyDescription);
    const brief =
      (space !== 'home' && space.description) || project.brief || '';

    const tryExample = () => {
      const currentExample = BRIEF_EXAMPLES.indexOf(companyDescription);
      const nextExample =
        currentExample >= 0
          ? currentExample + 1
          : Math.floor(Math.random() * BRIEF_EXAMPLES.length);
      spaceEditing?.setDescription(
        BRIEF_EXAMPLES[nextExample % BRIEF_EXAMPLES.length],
      );
    };

    const getPreferredPageNumber = (space: Space | 'home') => {
      return (
        (space === 'home'
          ? project.state.preferredNumPages
          : space.state.preferredNumPages) || DEFAULT_GENERATE_PAGE_COUNT
      );
    };

    const tasks = assistant.taskLog.tasksAffecting(
      space === 'home' ? project.homePage : space.subPages[0],
    );
    const isGenerating = tasks.some((t) => !t.completed);
    const pageCount = getPreferredPageNumber(space);
    const inWireframeMode = isWireframeOrDesign(editorModeStore.get());

    const generateSpace = () => {
      if (isGenerating) {
        return;
      }

      const isExample = BRIEF_EXAMPLES.indexOf(brief) !== -1;
      session.analytics.track({
        event: 'project:sitemap_generate',
        generate_page_count: pageCount,
        has_example_brief: isExample,
        has_generated_sitemap: false,
      });

      return assistant.generateSpace(space, {
        pageCount,
        generateCopy: inWireframeMode,
        chooseComponents: inWireframeMode,
      });
    };

    const maybeFocusCompanyDescription = React.useMemo(
      () => (companyDescription === '' ? refFocusTextarea : undefined),
      // refocus only when the selected space changes
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [space],
    );

    return (
      <Dialog onClose={onClose}>
        <div>
          <img
            src={logoImage}
            className={styles.relumeLogo}
            alt="Relume Library"
            role="presentation"
          />
          <TextMedium>
            <strong>Website designed & built faster with AI</strong>
          </TextMedium>
        </div>
        <TextRegularLight>
          Use AI as your design ally, not as a replacement. Effortlessly
          generate sitemaps and wireframes for marketing website in minutes.{' '}
          <HomeLink />
        </TextRegularLight>
        <DividerLine />
        <Rows spacing={16}>
          <Rows spacing={8}>
            <div className={styles.labelWithButton}>
              <TextTinyLight color="grey">Company description</TextTinyLight>
              <TextTinyLight color="grey">
                <Link onClick={tryExample}>Try example</Link>
              </TextTinyLight>
            </div>
            <TextSmallLight>
              <div className={styles.textareaContainer}>
                <div className={styles.textareaWrapper}>
                  <Textarea
                    ref={maybeFocusCompanyDescription}
                    value={companyDescription}
                    onChange={spaceEditing?.setDescription}
                    lang={locale}
                    placeholder="Describe the company in a sentence or two to generate a sitemap..."
                    minHeight={120}
                    allowNewlines
                    isInvalid={error != null && !hideError}
                    variant="invisible"
                  />
                </div>
              </div>
            </TextSmallLight>
            {error && !hideError && (
              <TextTinyLight color="grey">{error}</TextTinyLight>
            )}
            <LimitCounter
              value={companyDescription}
              limit={PROJECT_BRIEF_LIMIT}
            />
          </Rows>

          <div className={styles.generateButton}>
            <Rows spacing={8}>
              <Button
                label={
                  isGenerating
                    ? 'Generating...'
                    : [
                        'Generate',
                        inWireframeMode ? 'wireframe' : 'sitemap',
                      ].join(' ')
                }
                icon={IconStars}
                variant="primary"
                background="ai"
                tooltip={error}
                tooltipPlacement="horizontal"
                onClick={() => {
                  generateSpace();
                  onClose?.();
                }}
              />
            </Rows>
          </div>
        </Rows>
      </Dialog>
    );
  },
);

function HomeLink() {
  return (
    <Link target="_self" href={'/start'}>
      Learn more
    </Link>
  );
}

function refFocusTextarea(el: HTMLTextAreaElement | null) {
  if (el) el.focus();
}
